import { useEffect, useMemo, useState } from 'react';
import type { HijriConvertG2HResponse, HijriConvertH2GResponse } from '../lib/types';
import { readJson, writeJson } from '../lib/storage';
import { normalizeISODate, todayISO } from '../lib/time';
import { SettingsDrawer } from '../components/SettingsDrawer';
import { getEffectiveHijriAdjustment } from '../lib/hijriRegional';
import { hijriConvertG2H, hijriConvertH2G } from '../lib/api';
import { useSettingsState } from '../lib/useSettingsState';
import { useLocationState } from '../lib/useLocationState';
import { useLocale, hijriLabel } from '../lib/i18n';

export function ConverterPage() {
  const { t, locale } = useLocale();
  const { settings, setSettings } = useSettingsState();
  const { location } = useLocationState();
  const today = useMemo(() => todayISO(location?.timezone), [location?.timezone]);
  const effectiveHijriAdjustment = useMemo(() => getEffectiveHijriAdjustment(settings, location), [settings, location]);
  const [gDate, setGDate] = useState(() => normalizeISODate(readJson<string>('converter_g_date', today), today));
  const [hDay, setHDay] = useState(1);
  const [hMonth, setHMonth] = useState(9);
  const [hYear, setHYear] = useState(new Date().getFullYear());

  const [g2hOut, setG2hOut] = useState<HijriConvertG2HResponse | null>(null);
  const [h2gOut, setH2gOut] = useState<HijriConvertH2GResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => writeJson('converter_g_date', gDate), [gDate]);

  async function runG2H() {
    setBusy(true);
    setError(null);
    try {
      const res = await hijriConvertG2H(gDate, effectiveHijriAdjustment);
      setG2hOut(res);
      setH2gOut(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t.convertFailed);
    } finally {
      setBusy(false);
    }
  }

  async function runH2G() {
    setBusy(true);
    setError(null);
    try {
      const res = await hijriConvertH2G(hDay, hMonth, hYear);
      setH2gOut(res);
      setG2hOut(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t.convertFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
        <div className="col" style={{ gap: 6 }}>
          <h1 className="h1">{t.converterTitle}</h1>
          <div className="p">
            {t.converterIntro}
          </div>
        </div>
        <SettingsDrawer value={settings} location={location} onChange={setSettings} />
      </div>

      {error ? (
        <div className="card" style={{ padding: 14, marginTop: 14, borderColor: 'var(--danger-border)' }}>
          <div className="h2" style={{ marginBottom: 6 }}>{t.errorWord}</div>
          <div className="p">{error}</div>
        </div>
      ) : null}

      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="h2" style={{ marginBottom: 8 }}>{t.g2hTitle}</div>
          <div className="p" style={{ fontSize: 12, marginBottom: 8 }}>{t.g2hSub}</div>
          <input className="input" type="date" value={gDate} onChange={(e) => setGDate(normalizeISODate(e.target.value, gDate))} />
          <div className="row" style={{ marginTop: 10, flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => void runG2H()} aria-disabled={busy} disabled={busy}>{t.convert}</button>
            <button className="btn" onClick={() => setGDate(today)}>{t.today}</button>
            <span className="chip">{t.adjustmentPrefix}: {effectiveHijriAdjustment > 0 ? `+${effectiveHijriAdjustment}` : String(effectiveHijriAdjustment)}</span>
          </div>
          {g2hOut ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>
                {hijriLabel(locale, g2hOut.output.dateHijri)}
              </div>
              <div className="p" style={{ fontSize: 12 }}>{t.weekdayPrefix}: {g2hOut.output.dateHijri.weekdayName}</div>
            </div>
          ) : null}
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div className="h2" style={{ marginBottom: 8 }}>{t.h2gTitle}</div>
          <div className="p" style={{ fontSize: 12, marginBottom: 8 }}>{t.h2gSub}</div>

          <div className="row" style={{ gap: 8 }}>
            <input className="input" type="number" min={1} max={30} value={hDay} onChange={(e) => setHDay(Number(e.target.value))} aria-label={t.hijriDayAria} />
            <input className="input" type="number" min={1} max={12} value={hMonth} onChange={(e) => setHMonth(Number(e.target.value))} aria-label={t.hijriMonthAria} />
            <input className="input" type="number" min={1300} max={1700} value={hYear} onChange={(e) => setHYear(Number(e.target.value))} aria-label={t.hijriYearAria} />
          </div>

          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn" onClick={() => void runH2G()} aria-disabled={busy} disabled={busy}>{t.convert}</button>
          </div>

          {h2gOut ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{h2gOut.output.dateGregorianISO}</div>
              <div className="p" style={{ fontSize: 12 }}>
                {t.h2gNote}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginTop: 16 }}>
        <div className="p">
          <strong>{t.disclaimerLabel}:</strong> {t.converterDisclaimer}
        </div>
      </div>
    </div>
  );
}
